import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import admissionsService from '../../services/admissionsService';
import classesService from '../../services/classesService';
import schoolsService from '../../services/schoolsService';
import DialogModal from '../../components/DialogModal';
import { MagnifyingGlass, Plus, PencilSimple, Trash, UserSwitch, Copy, Link as LinkIcon, Paperclip, CheckCircle, DownloadSimple, ClipboardText } from '@phosphor-icons/react';
import EnterpriseAdmissionsPanel from '../../components/EnterpriseAdmissionsPanel';

const backendBase = (process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8000').replace(/\/+$/, '');
const buildFileUrl = (path) => (path?.startsWith('http') ? path : `${backendBase}${path?.startsWith('/') ? path : `/${path}`}`);

const DOCUMENT_TYPE_LABELS = {
  birth_certificate: 'Birth Certificate',
  previous_report_card: 'Previous Report Card',
  passport_photo: 'Passport Photo',
  other: 'Other',
};

const STATUSES = [
  { value: 'inquiry', label: 'Inquiry' },
  { value: 'applied', label: 'Applied' },
  { value: 'interview_scheduled', label: 'Interview Scheduled' },
  { value: 'entrance_test_scheduled', label: 'Entrance Test Scheduled' },
  { value: 'entrance_test_completed', label: 'Entrance Test Completed' },
  { value: 'offered', label: 'Offered' },
  { value: 'enrolled', label: 'Enrolled' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'waitlisted', label: 'Waitlisted' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const emptyForm = {
  first_name: '',
  last_name: '',
  other_names: '',
  date_of_birth: '',
  gender: 'male',
  guardian_name: '',
  guardian_relationship: 'Parent',
  guardian_phone: '',
  guardian_email: '',
  applying_for_class_id: '',
  entrance_test_date: '',
  application_fee_amount: '',
  notes: '',
};

function AdmissionsApplicantsPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [applicants, setApplicants] = useState([]);
  const [filteredApplicants, setFilteredApplicants] = useState([]);
  const [classes, setClasses] = useState([]);
  const [schoolCode, setSchoolCode] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [convertTarget, setConvertTarget] = useState(null);
  const [convertClassId, setConvertClassId] = useState('');
  const [convertDate, setConvertDate] = useState(new Date().toISOString().split('T')[0]);
  const [converting, setConverting] = useState(false);
  const [documentsTarget, setDocumentsTarget] = useState(null);
  const [applicantDocuments, setApplicantDocuments] = useState([]);
  const [loadingDocuments, setLoadingDocuments] = useState(false);
  const [promptDialog, setPromptDialog] = useState(null); // { title, message, onSubmit }
  const [reviewsTarget, setReviewsTarget] = useState(null);
  const [applicantReviews, setApplicantReviews] = useState([]);
  const [loadingReviews, setLoadingReviews] = useState(false);
  const [reviewCriteria, setReviewCriteria] = useState([{ name: '', weight: 1, score: 0 }]);
  const [reviewRecommendation, setReviewRecommendation] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (!user?.school_id) return;
    schoolsService.getSchool(user.school_id)
      .then(data => setSchoolCode(data?.code || null))
      .catch(() => {});
  }, [user?.school_id]);

  const publicApplyUrl = schoolCode ? `${window.location.origin}/apply/${schoolCode}` : null;

  const copyApplyLink = () => {
    if (!publicApplyUrl) return;
    navigator.clipboard.writeText(publicApplyUrl)
      .then(() => toast.success('Application link copied'))
      .catch(() => toast.error('Failed to copy link'));
  };

  useEffect(() => {
    let filtered = applicants;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (a) =>
          `${a.first_name} ${a.last_name}`.toLowerCase().includes(term) ||
          a.guardian_name.toLowerCase().includes(term) ||
          a.guardian_phone.toLowerCase().includes(term)
      );
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter((a) => a.status === statusFilter);
    }
    setFilteredApplicants(filtered);
  }, [applicants, searchTerm, statusFilter]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [applicantsData, classesData] = await Promise.all([
        admissionsService.listApplicants(),
        classesService.listClasses().catch(() => []),
      ]);
      setApplicants(Array.isArray(applicantsData) ? applicantsData : []);
      setClasses(Array.isArray(classesData) ? classesData : []);
    } catch (error) {
      toast.error('Failed to load applicants');
    } finally {
      setLoading(false);
    }
  };

  const getClassName = (classId) => {
    const cls = classes.find((c) => c.id === classId);
    return cls ? cls.class_name || cls.name : '-';
  };

  const openCreateModal = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setShowModal(true);
  };

  const openEditModal = (applicant) => {
    setEditingId(applicant.id);
    setFormData({
      first_name: applicant.first_name,
      last_name: applicant.last_name,
      other_names: applicant.other_names || '',
      date_of_birth: applicant.date_of_birth,
      gender: applicant.gender,
      guardian_name: applicant.guardian_name,
      guardian_relationship: applicant.guardian_relationship,
      guardian_phone: applicant.guardian_phone,
      guardian_email: applicant.guardian_email || '',
      applying_for_class_id: applicant.applying_for_class_id || '',
      entrance_test_date: applicant.entrance_test_date || '',
      application_fee_amount: applicant.application_fee_amount || '',
      notes: applicant.notes || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.first_name || !formData.last_name || !formData.guardian_name || !formData.guardian_phone) {
      toast.error('Please fill in all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        ...formData,
        applying_for_class_id: formData.applying_for_class_id || null,
        application_fee_amount: formData.application_fee_amount ? Number(formData.application_fee_amount) : null,
      };
      if (editingId) {
        await admissionsService.updateApplicant(editingId, payload);
        toast.success('Applicant updated');
      } else {
        await admissionsService.createApplicant(payload);
        toast.success('Applicant registered');
      }
      setShowModal(false);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to save applicant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStatusChange = async (applicant, newStatus) => {
    const needsReason = ['rejected', 'waitlisted', 'withdrawn'].includes(newStatus);
    if (needsReason) {
      setPromptDialog({
        title: 'Reason Required',
        message: `Reason for marking this applicant as ${newStatus.replace('_', ' ')}:`,
        onSubmit: async (value) => {
          const reason = value?.trim();
          if (!reason) {
            toast.error('A reason is required for this status change');
            return;
          }
          try {
            await admissionsService.changeStage(applicant.id, { status: newStatus, reason });
            toast.success('Status updated');
            loadData();
          } catch (error) {
            toast.error(error.response?.data?.detail || error.message || 'Failed to update status');
          }
        },
      });
      return;
    }
    try {
      await admissionsService.updateApplicant(applicant.id, { status: newStatus });
      toast.success('Status updated');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to update status');
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleteLoading(true);
    try {
      await admissionsService.deleteApplicant(deleteTarget);
      toast.success('Applicant deleted');
      setDeleteTarget(null);
      loadData();
    } catch (error) {
      toast.error('Failed to delete applicant');
    } finally {
      setDeleteLoading(false);
    }
  };

  const openDocumentsModal = async (applicant) => {
    setDocumentsTarget(applicant);
    setLoadingDocuments(true);
    try {
      const docs = await admissionsService.listApplicantDocuments(applicant.id);
      setApplicantDocuments(Array.isArray(docs) ? docs : []);
    } catch (error) {
      toast.error('Failed to load documents');
      setApplicantDocuments([]);
    } finally {
      setLoadingDocuments(false);
    }
  };

  const verifyApplicantDocument = async (documentId, status, notes = null) => {
    try {
      await admissionsService.verifyDocument(documentId, status, notes);
      setApplicantDocuments((documents) => documents.map((doc) => doc.id === documentId ? { ...doc, verification_status: status, verification_notes: notes || null } : doc));
      toast.success(`Document ${status}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update document verification');
    }
  };

  const handleVerifyDocument = (documentId, status) => {
    if (status === 'rejected') {
      setPromptDialog({
        title: 'Reject Document',
        message: 'Reason for rejecting this document:',
        onSubmit: (value) => {
          const notes = value?.trim();
          if (!notes) return;
          verifyApplicantDocument(documentId, status, notes);
        },
      });
      return;
    }
    verifyApplicantDocument(documentId, status);
  };

  const openReviewsModal = async (applicant) => {
    setReviewsTarget(applicant);
    setReviewCriteria([{ name: '', weight: 1, score: 0 }]);
    setReviewRecommendation('');
    setLoadingReviews(true);
    try {
      const reviews = await admissionsService.listReviews(applicant.id);
      setApplicantReviews(Array.isArray(reviews) ? reviews : []);
    } catch (error) {
      toast.error('Failed to load reviews');
      setApplicantReviews([]);
    } finally {
      setLoadingReviews(false);
    }
  };

  const updateReviewCriterion = (index, field, value) => {
    setReviewCriteria((criteria) =>
      criteria.map((c, i) => (i === index ? { ...c, [field]: value } : c))
    );
  };

  const addReviewCriterion = () => {
    setReviewCriteria((criteria) => [...criteria, { name: '', weight: 1, score: 0 }]);
  };

  const removeReviewCriterion = (index) => {
    setReviewCriteria((criteria) => criteria.filter((_, i) => i !== index));
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    const cleaned = reviewCriteria
      .filter((c) => c.name.trim())
      .map((c) => ({ name: c.name.trim(), weight: Number(c.weight) || 0, score: Number(c.score) || 0 }));
    if (cleaned.length === 0) {
      toast.error('At least one rubric criterion is required');
      return;
    }
    setSubmittingReview(true);
    try {
      await admissionsService.createReview(reviewsTarget.id, {
        criteria: cleaned,
        recommendation: reviewRecommendation || null,
      });
      toast.success('Review recorded');
      const reviews = await admissionsService.listReviews(reviewsTarget.id);
      setApplicantReviews(Array.isArray(reviews) ? reviews : []);
      setReviewCriteria([{ name: '', weight: 1, score: 0 }]);
      setReviewRecommendation('');
    } catch (error) {
      toast.error(error.response?.data?.detail || error.message || 'Failed to record review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const openConvertModal = (applicant) => {
    setConvertTarget(applicant);
    setConvertClassId(applicant.applying_for_class_id || '');
    setConvertDate(new Date().toISOString().split('T')[0]);
  };

  const handleConvert = async () => {
    if (!convertTarget || !convertClassId || !convertDate) {
      toast.error('Select a class and admission date');
      return;
    }
    setConverting(true);
    try {
      const result = await admissionsService.convertApplicant(convertTarget.id, {
        class_id: convertClassId,
        admission_date: convertDate,
      });
      toast.success(`Enrolled as student ${result.student_number}`);
      setConvertTarget(null);
      loadData();
    } catch (error) {
      toast.error(error.message || 'Failed to convert applicant');
    } finally {
      setConverting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading">Loading applicants...</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="page">
        <div className="page-header">
          <div>
            <h1>Admissions — Applicants</h1>
            <p>Track prospective students from inquiry through enrollment</p>
          </div>
          <button className="btn btn-primary" onClick={openCreateModal}>
            <Plus size={20} />
            Register Applicant
          </button>
        </div>

        {publicApplyUrl && (
          <div className="form-card" style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px', marginBottom: 16 }}>
            <LinkIcon size={18} />
            <span style={{ fontSize: 14 }}>Public application form:</span>
            <code style={{ fontSize: 13, background: '#f0f0f0', padding: '2px 8px', borderRadius: 4 }}>{publicApplyUrl}</code>
            <button className="btn btn-sm" type="button" onClick={copyApplyLink} title="Copy link">
              <Copy size={14} /> Copy
            </button>
          </div>
        )}

        <div className="stats-bar">
          <div className="stat">
            <h3>Total Applicants</h3>
            <p className="stat-value">{applicants.length}</p>
          </div>
          <div className="stat">
            <h3>Offered</h3>
            <p className="stat-value">{applicants.filter((a) => a.status === 'offered').length}</p>
          </div>
          <div className="stat">
            <h3>Enrolled</h3>
            <p className="stat-value">{applicants.filter((a) => a.status === 'enrolled').length}</p>
          </div>
          <EnterpriseAdmissionsPanel applicants={applicants} classes={classes} schoolCode={schoolCode} />
        </div>

        <div className="filters-bar">
          <div className="search-input">
            <MagnifyingGlass size={20} />
            <input
              type="text"
              placeholder="Search by name, guardian, phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className="filter-select">
            <option value="all">All Statuses</option>
            {STATUSES.map((s) => (
              <option key={s.value} value={s.value}>{s.label}</option>
            ))}
          </select>
        </div>

        <div className="table-container">
          {filteredApplicants.length === 0 ? (
            <div className="empty-state">
              <p>No applicants found</p>
            </div>
          ) : (
            <table className="data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Guardian</th>
                  <th>Phone</th>
                  <th>Applying For</th>
                  <th>Fee</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredApplicants.map((applicant) => (
                  <tr key={applicant.id}>
                    <td>{applicant.first_name} {applicant.last_name}</td>
                    <td>{applicant.guardian_name} ({applicant.guardian_relationship})</td>
                    <td>{applicant.guardian_phone}</td>
                    <td>{getClassName(applicant.applying_for_class_id)}</td>
                    <td>
                      {applicant.application_fee_amount ? (
                        applicant.application_fee_paid ? (
                          <span style={{ display: 'flex', alignItems: 'center', gap: 4, color: '#065F46', fontSize: 12 }}>
                            <CheckCircle size={14} weight="fill" /> Paid
                          </span>
                        ) : (
                          <span style={{ fontSize: 12, color: '#9A3412' }}>Unpaid</span>
                        )
                      ) : (
                        <span style={{ fontSize: 12, color: '#A1A1AA' }}>—</span>
                      )}
                    </td>
                    <td>
                      <select
                        value={applicant.status}
                        onChange={(e) => handleStatusChange(applicant, e.target.value)}
                        disabled={!!applicant.converted_student_id}
                        className="filter-select"
                      >
                        {STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="action-buttons">
                      <button className="btn-icon" title="Documents" onClick={() => openDocumentsModal(applicant)}>
                        <Paperclip size={18} />
                      </button>
                      <button className="btn-icon" title="Reviews" onClick={() => openReviewsModal(applicant)}>
                        <ClipboardText size={18} />
                      </button>
                      {applicant.status === 'offered' && !applicant.converted_student_id && (
                        <button
                          className="btn-icon btn-primary"
                          title="Convert to Student"
                          onClick={() => openConvertModal(applicant)}
                        >
                          <UserSwitch size={18} />
                        </button>
                      )}
                      <button className="btn-icon" title="Edit" onClick={() => openEditModal(applicant)}>
                        <PencilSimple size={18} />
                      </button>
                      <button
                        className="btn-icon btn-danger"
                        title="Delete"
                        onClick={() => setDeleteTarget(applicant.id)}
                      >
                        <Trash size={18} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h2>{editingId ? 'Edit Applicant' : 'Register Applicant'}</h2>
              <button type="button" className="btn-close" onClick={() => setShowModal(false)} aria-label="Close">×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} required />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date of Birth *</label>
                  <input type="date" value={formData.date_of_birth} onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Gender *</label>
                  <select value={formData.gender} onChange={(e) => setFormData({ ...formData, gender: e.target.value })} required>
                    <option value="male">Male</option>
                    <option value="female">Female</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Applying For Class</label>
                <select value={formData.applying_for_class_id} onChange={(e) => setFormData({ ...formData, applying_for_class_id: e.target.value })}>
                  <option value="">Select Class</option>
                  {classes.map((c) => (
                    <option key={c.id} value={c.id}>{c.class_name || c.name}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Guardian Name *</label>
                  <input type="text" value={formData.guardian_name} onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Relationship *</label>
                  <select value={formData.guardian_relationship} onChange={(e) => setFormData({ ...formData, guardian_relationship: e.target.value })} required>
                    <option value="Parent">Parent</option>
                    <option value="Guardian">Guardian</option>
                    <option value="Sibling">Sibling</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Guardian Phone *</label>
                  <input type="tel" value={formData.guardian_phone} onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })} required />
                </div>
                <div className="form-group">
                  <label>Guardian Email</label>
                  <input type="email" value={formData.guardian_email} onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Entrance Test Date</label>
                  <input type="date" value={formData.entrance_test_date} onChange={(e) => setFormData({ ...formData, entrance_test_date: e.target.value })} />
                </div>
                <div className="form-group">
                  <label>Application Fee (GHS)</label>
                  <input type="number" step="0.01" value={formData.application_fee_amount} onChange={(e) => setFormData({ ...formData, application_fee_amount: e.target.value })} />
                </div>
              </div>

              <div className="form-group">
                <label>Notes</label>
                <textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} />
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingId ? 'Update' : 'Register'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Convert to Student Modal */}
      {convertTarget && (
        <div className="modal-overlay" onClick={() => setConvertTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 400 }}>
            <div className="modal-header">
              <h2>Convert to Student</h2>
              <button type="button" className="btn-close" onClick={() => setConvertTarget(null)} aria-label="Close">×</button>
            </div>
            <p style={{ margin: '0 0 16px' }}>
              Enroll <strong>{convertTarget.first_name} {convertTarget.last_name}</strong> as a student.
            </p>
            <div className="form-group">
              <label>Class *</label>
              <select value={convertClassId} onChange={(e) => setConvertClassId(e.target.value)} required>
                <option value="">Select Class</option>
                {classes.map((c) => (
                  <option key={c.id} value={c.id}>{c.class_name || c.name}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label>Admission Date *</label>
              <input type="date" value={convertDate} onChange={(e) => setConvertDate(e.target.value)} required />
            </div>
            <div className="form-actions">
              <button type="button" className="btn btn-primary" onClick={handleConvert} disabled={converting}>
                {converting ? 'Converting...' : 'Convert to Student'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={() => setConvertTarget(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Documents Modal */}
      {documentsTarget && (
        <div className="modal-overlay" onClick={() => setDocumentsTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <div className="modal-header">
              <h2>Documents — {documentsTarget.first_name} {documentsTarget.last_name}</h2>
              <button type="button" className="btn-close" onClick={() => setDocumentsTarget(null)} aria-label="Close">×</button>
            </div>
            {loadingDocuments ? (
              <p>Loading...</p>
            ) : applicantDocuments.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No documents were uploaded with this application.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {applicantDocuments.map((doc) => (
                  <div key={doc.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid #E5E5E0', borderRadius: 8, padding: '8px 12px' }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600 }}>{DOCUMENT_TYPE_LABELS[doc.document_type] || doc.document_type}</div>
                      <div style={{ fontSize: 12, color: '#6b7280' }}>{doc.original_filename} · {(doc.file_size / 1024).toFixed(0)} KB</div>
                    </div>
                    <a href={buildFileUrl(doc.file_path)} target="_blank" rel="noreferrer" className="btn-icon" title="Download">
                      <DownloadSimple size={18} />
                    </a>
                    <span className="text-capitalize" style={{ fontSize: 12 }}>{doc.verification_status || 'pending'}</span>
                    {doc.verification_status !== 'verified' && <button type="button" className="btn btn-sm btn-outline-success" onClick={() => handleVerifyDocument(doc.id, 'verified')}>Verify</button>}
                    {doc.verification_status !== 'rejected' && <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => handleVerifyDocument(doc.id, 'rejected')}>Reject</button>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Application Review Rubric Modal */}
      {reviewsTarget && (
        <div className="modal-overlay" onClick={() => setReviewsTarget(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>Reviews — {reviewsTarget.first_name} {reviewsTarget.last_name}</h2>
              <button type="button" className="btn-close" onClick={() => setReviewsTarget(null)} aria-label="Close">×</button>
            </div>

            {loadingReviews ? (
              <p>Loading...</p>
            ) : applicantReviews.length === 0 ? (
              <p style={{ color: '#6b7280' }}>No reviews recorded yet.</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
                {applicantReviews.map((review) => (
                  <div key={review.id} style={{ border: '1px solid #E5E5E0', borderRadius: 8, padding: '8px 12px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, fontWeight: 600 }}>
                      <span>Score: {review.total_score.toFixed(2)}</span>
                      {review.recommendation && <span className="text-capitalize">{review.recommendation}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                      {(review.criteria || []).map((c) => `${c.name}: ${c.score} (×${c.weight})`).join(' · ')}
                    </div>
                    <div style={{ fontSize: 11, color: '#A1A1AA', marginTop: 4 }}>
                      {new Date(review.created_at).toLocaleString()}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={handleSubmitReview}>
              <label style={{ fontWeight: 600, fontSize: 14 }}>New Review</label>
              {reviewCriteria.map((criterion, index) => (
                <div className="form-row" key={index} style={{ alignItems: 'flex-end' }}>
                  <div className="form-group" style={{ flex: 2 }}>
                    <label>Criterion</label>
                    <input
                      type="text"
                      placeholder="e.g. Academic readiness"
                      value={criterion.name}
                      onChange={(e) => updateReviewCriterion(index, 'name', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Weight</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={criterion.weight}
                      onChange={(e) => updateReviewCriterion(index, 'weight', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Score</label>
                    <input
                      type="number"
                      min="0"
                      step="0.1"
                      value={criterion.score}
                      onChange={(e) => updateReviewCriterion(index, 'score', e.target.value)}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn-icon"
                    title="Remove criterion"
                    onClick={() => removeReviewCriterion(index)}
                    disabled={reviewCriteria.length === 1}
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
              <button type="button" className="btn btn-sm btn-secondary" onClick={addReviewCriterion} style={{ marginBottom: 12 }}>
                <Plus size={14} /> Add Criterion
              </button>

              <div className="form-group">
                <label>Recommendation</label>
                <select value={reviewRecommendation} onChange={(e) => setReviewRecommendation(e.target.value)}>
                  <option value="">None</option>
                  <option value="admit">Admit</option>
                  <option value="waitlist">Waitlist</option>
                  <option value="reject">Reject</option>
                </select>
              </div>

              <div className="form-actions">
                <button type="submit" className="btn btn-primary" disabled={submittingReview}>
                  {submittingReview ? 'Saving...' : 'Submit Review'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={() => setReviewsTarget(null)}>Close</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <DialogModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        title="Delete Applicant"
        message="This will permanently delete this applicant record. Continue?"
        type="warning"
        isLoading={deleteLoading}
        primaryAction={{ label: 'Delete', onClick: confirmDelete, loadingLabel: 'Deleting...' }}
        secondaryAction={{ label: 'Cancel' }}
      />

      <DialogModal
        isOpen={!!promptDialog}
        onClose={() => setPromptDialog(null)}
        title={promptDialog?.title}
        message={promptDialog?.message}
        type="warning"
        input={{ placeholder: 'Reason', required: true }}
        primaryAction={{ label: 'Submit', onClick: (value) => promptDialog?.onSubmit(value) }}
        secondaryAction={{ label: 'Cancel' }}
      />
    </Layout>
  );
}

export default AdmissionsApplicantsPage;
